import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listRecentTrackingEvents,
  type TrackingDebugRow,
} from "@/lib/tracking-debug.functions";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Activity, Loader2, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/admin/tracking-debug")({
  head: () => ({
    meta: [
      { title: "Tracking debug - AutoXChange Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: TrackingDebugPage,
  errorComponent: ({ error }) => {
    const router = useRouter();
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Fejl
            </CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => router.invalidate()}>Prøv igen</Button>
          </CardContent>
        </Card>
      </div>
    );
  },
});

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("da-DK", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ChannelBadge({ channel }: { channel: string }) {
  if (channel === "meta_capi") {
    return (
      <Badge variant="outline" className="border-blue-500/50 text-blue-700 dark:text-blue-400">
        Meta CAPI
      </Badge>
    );
  }
  if (channel === "ga4_mp") {
    return (
      <Badge variant="outline" className="border-orange-500/50 text-orange-700 dark:text-orange-400">
        GA4 MP
      </Badge>
    );
  }
  return <Badge variant="outline">{channel}</Badge>;
}

function StatusBadge({ status, http }: { status: string; http: number | null }) {
  if (status === "ok") {
    return (
      <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-600/90">
        <CheckCircle2 className="h-3 w-3" />
        OK {http ? `(${http})` : ""}
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1">
      <AlertCircle className="h-3 w-3" />
      {status} {http ? `(${http})` : ""}
    </Badge>
  );
}

type MatchKeyMap = Record<string, boolean> | null;

function MatchKeyDots({ keys }: { keys: MatchKeyMap }) {
  if (!keys || typeof keys !== "object") return <span className="text-muted-foreground">—</span>;
  const entries = Object.entries(keys);
  if (entries.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, present]) => (
        <span
          key={k}
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono ${
            present
              ? "bg-green-500/15 text-green-700 dark:text-green-400"
              : "bg-muted text-muted-foreground line-through"
          }`}
          title={present ? `${k} sent` : `${k} mangler`}
        >
          {k}
        </span>
      ))}
    </div>
  );
}

function EventRow({ row }: { row: TrackingDebugRow }) {
  const matchKeys = (row.has_match_keys ?? null) as MatchKeyMap;
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
        {formatTime(row.created_at)}
      </TableCell>
      <TableCell>
        <ChannelBadge channel={row.channel} />
      </TableCell>
      <TableCell className="font-medium">{row.event_name}</TableCell>
      <TableCell>
        {row.event_id ? (
          <code
            className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]"
            title={row.event_id}
          >
            {row.event_id.slice(0, 8)}…
          </code>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        {row.source ? (
          <Badge variant="secondary" className="text-[10px]">
            {row.source}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <StatusBadge status={row.status} http={row.http_status} />
      </TableCell>
      <TableCell>
        <MatchKeyDots keys={matchKeys} />
      </TableCell>
      <TableCell className="max-w-[260px]">
        {row.error_message ? (
          <code
            className="block max-w-[260px] truncate rounded bg-destructive/10 px-1.5 py-0.5 font-mono text-[10px] text-destructive"
            title={row.error_message}
          >
            {row.error_message}
          </code>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function TrackingDebugPage() {
  const fetchEvents = useServerFn(listRecentTrackingEvents);
  const query = useQuery({
    queryKey: ["tracking-debug-events"],
    queryFn: () => fetchEvents(),
    refetchInterval: 15_000,
    staleTime: 5_000,
  });

  const rows = query.data?.rows ?? [];
  const testActive = query.data?.testEventCodeActive ?? false;

  // Summary
  const totalOk = rows.filter((r) => r.status === "ok").length;
  const totalFailed = rows.length - totalOk;

  return (
    <div className="container mx-auto space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Activity className="h-6 w-6" />
            Tracking debug
          </h1>
          <p className="text-sm text-muted-foreground">
            Seneste 50 server-fyrede CAPI/GA4 events. Auto-opdateres hvert 15. sekund.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {testActive ? (
            <Badge variant="outline" className="border-yellow-500/50 text-yellow-700 dark:text-yellow-400">
              ⚠️ META_CAPI_TEST_EVENT_CODE aktiv
            </Badge>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Genindlæs
          </Button>
        </div>
      </div>

      {testActive ? (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardContent className="py-3 text-sm">
            <strong>Test-tilstand aktiv:</strong> Events sendes med{" "}
            <code className="rounded bg-muted px-1 font-mono text-xs">test_event_code</code> og
            optræder i Meta Events Manager → <em>Test Events</em>, ikke i live-rapportering. Fjern
            secret når du er færdig med at debugge.
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total events</CardDescription>
            <CardTitle className="text-2xl">{rows.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>OK</CardDescription>
            <CardTitle className="text-2xl text-green-600">{totalOk}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Fejlede</CardDescription>
            <CardTitle className={`text-2xl ${totalFailed > 0 ? "text-destructive" : ""}`}>
              {totalFailed}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Seneste events</CardTitle>
          <CardDescription>
            Viser alle server-side conversion-sends fra Meta CAPI og GA4 Measurement Protocol.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Henter events…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              Ingen events endnu. Indsend en kontaktforespørgsel eller udfyld bil-forespørgsel for at
              generere et event.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tid</TableHead>
                    <TableHead>Kanal</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Event ID</TableHead>
                    <TableHead>Kilde</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Match keys</TableHead>
                    <TableHead>Fejl</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <EventRow key={row.id} row={row} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground">
        <Link to="/admin" className="underline hover:text-foreground">
          ← Tilbage til admin
        </Link>
      </div>
    </div>
  );
}
